/**
 * Static display metadata for the Support module.
 *
 * Stages are NOT here — they're user-configurable rows fetched from
 * `/api/support/stages`. This file holds the fixed enums (category, priority,
 * assignee names) and the bridge from a stage's hex color to the KanbanStage
 * `accent` slot.
 */
import type { ChipVariant } from '@/components/ui'
import type { Assignee, Category, Priority } from '../api/types'

export const CATEGORY_LABELS: Record<Category, string> = {
  order_status: 'Order Status',
  shipping_delivery: 'Shipping / Delivery',
  quality_complaint: 'Quality Complaint',
  payment_billing: 'Payment / Billing',
  freight_coordination: 'Freight Coordination',
  pricing_inquiry: 'Pricing / Quote',
  returns_rma: 'Returns / RMA',
  general_inquiry: 'General Inquiry',
  noise: 'Noise / No Action',
}

export const CATEGORY_SHORT: Record<Category, string> = {
  order_status: 'Order',
  shipping_delivery: 'Shipping',
  quality_complaint: 'Quality',
  payment_billing: 'Billing',
  freight_coordination: 'Freight',
  pricing_inquiry: 'Pricing',
  returns_rma: 'Returns',
  general_inquiry: 'General',
  noise: 'Noise',
}

// Category badge color (spec §9.2). Mapped onto the Chip variant enum.
export const CATEGORY_VARIANT: Record<Category, ChipVariant> = {
  order_status: 'info',
  shipping_delivery: 'warning',
  quality_complaint: 'danger',
  payment_billing: 'accent',
  freight_coordination: 'info',
  pricing_inquiry: 'neutral',
  returns_rma: 'warning',
  general_inquiry: 'neutral',
  noise: 'neutral',
}

export const CATEGORY_OPTIONS: { value: Category; label: string }[] = (
  Object.keys(CATEGORY_LABELS) as Category[]
).map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

export const PRIORITY_VARIANT: Record<Priority, ChipVariant> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  urgent: 'danger',
}

export const PRIORITY_OPTIONS: { value: Priority; label: string }[] = (
  ['urgent', 'high', 'medium', 'low'] as Priority[]
).map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))

// Support team roster (spec §2). Order drives the assignee quick-filter tabs.
export const ASSIGNEE_NAMES: Record<Assignee, string> = {
  ryan: 'Ryan Norris',
  emma: 'Emma Cowart',
  ella: 'Ella Johnson',
  michelle: 'Michelle Holmes',
  frank: 'Frank Rodriguez',
  shamola: 'Shamola Jaiyeoba',
}

export const ASSIGNEE_SHORT: Record<Assignee, string> = {
  ryan: 'Ryan',
  emma: 'Emma',
  ella: 'Ella',
  michelle: 'Michelle',
  frank: 'Frank',
  shamola: 'Shamola',
}

export const ASSIGNEE_ORDER: Assignee[] = [
  'ryan', 'emma', 'ella', 'michelle', 'frank', 'shamola',
]

/**
 * Map a stage's color (server-chosen, arbitrary) onto the 4-value KanbanStage
 * `accent` enum. We classify by dominant RGB channel rather than matching
 * literal color values (the tokens-only contract bans hardcoded color literals
 * under src/). The full color is surfaced via a `--stage-color` CSS custom
 * property on the card — that's user data, not a design token.
 */
export function accentForHex(
  color: string | null,
): 'neutral' | 'success' | 'warning' | 'danger' {
  const rgb = parseRgb(color)
  if (!rgb) return 'neutral'
  const [r, g, b] = rgb
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  if (max - min < 40) return 'neutral'
  if (r >= g && r >= b) {
    return g > b + 40 ? 'warning' : 'danger'
  }
  if (g >= r && g >= b) return 'success'
  return 'neutral'
}

function parseRgb(color: string | null): [number, number, number] | null {
  if (!color) return null
  const s = color.trim().replace(/^#/, '')
  if (s.length === 3) {
    const r = parseInt(s[0] + s[0], 16)
    const g = parseInt(s[1] + s[1], 16)
    const b = parseInt(s[2] + s[2], 16)
    return Number.isNaN(r + g + b) ? null : [r, g, b]
  }
  if (s.length === 6) {
    const r = parseInt(s.slice(0, 2), 16)
    const g = parseInt(s.slice(2, 4), 16)
    const b = parseInt(s.slice(4, 6), 16)
    return Number.isNaN(r + g + b) ? null : [r, g, b]
  }
  return null
}
